import { adminControllerChangePassword } from "@/services/admin/admin";
import { LockOutlined } from "@ant-design/icons";
import { ProForm, ProFormText } from "@ant-design/pro-components";
import { FormattedMessage, useIntl } from "@umijs/max";
import { App, Button, Modal } from "antd";
import React, { useState } from "react";
import styles from "./ChangePasswordModal.less";

interface ChangePasswordModalProps {
  open: boolean;
  onCancel: () => void;
}

/**
 * 修改密码弹框组件
 */
const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onCancel,
}) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const [form] = ProForm.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: API.ChangePasswordDto) => {
    try {
      setLoading(true);
      await adminControllerChangePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success(
        intl.formatMessage({
          id: "pages.settings.password.success",
          defaultMessage: "密码修改成功！",
        })
      );
      form.resetFields();
      onCancel();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({
          id: "pages.settings.password.failure",
          defaultMessage: "密码修改失败，请重试！",
        });
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: "pages.settings.password.title",
        defaultMessage: "修改密码",
      })}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={520}
      destroyOnClose
    >
      <div className={styles["change-password-modal"]}>
        <ProForm
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          submitter={{
            render: (_, dom) => (
              <div className={styles["form-actions"]}>
                <Button onClick={handleCancel}>
                  <FormattedMessage
                    id="pages.settings.password.cancel"
                    defaultMessage="取消"
                  />
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  <FormattedMessage
                    id="pages.settings.password.submit"
                    defaultMessage="确认修改"
                  />
                </Button>
              </div>
            ),
          }}
        >
          <ProFormText.Password
            name="oldPassword"
            label={intl.formatMessage({
              id: "pages.settings.password.oldPassword",
              defaultMessage: "旧密码",
            })}
            fieldProps={{
              size: "large",
              prefix: <LockOutlined />,
              placeholder: intl.formatMessage({
                id: "pages.settings.password.oldPassword.placeholder",
                defaultMessage: "请输入旧密码",
              }),
            }}
            rules={[
              {
                required: true,
                message: (
                  <FormattedMessage
                    id="pages.settings.password.oldPassword.required"
                    defaultMessage="请输入旧密码！"
                  />
                ),
              },
            ]}
          />

          <ProFormText.Password
            name="newPassword"
            label={intl.formatMessage({
              id: "pages.settings.password.newPassword",
              defaultMessage: "新密码",
            })}
            fieldProps={{
              size: "large",
              prefix: <LockOutlined />,
              placeholder: intl.formatMessage({
                id: "pages.settings.password.newPassword.placeholder",
                defaultMessage: "请输入新密码（至少6位）",
              }),
            }}
            rules={[
              {
                required: true,
                message: (
                  <FormattedMessage
                    id="pages.settings.password.newPassword.required"
                    defaultMessage="请输入新密码！"
                  />
                ),
              },
              {
                min: 6,
                message: (
                  <FormattedMessage
                    id="pages.settings.password.newPassword.minLength"
                    defaultMessage="新密码长度不能少于6位！"
                  />
                ),
              },
            ]}
          />

          <ProFormText.Password
            name="confirmPassword"
            label={intl.formatMessage({
              id: "pages.settings.password.confirmPassword",
              defaultMessage: "确认新密码",
            })}
            fieldProps={{
              size: "large",
              prefix: <LockOutlined />,
              placeholder: intl.formatMessage({
                id: "pages.settings.password.confirmPassword.placeholder",
                defaultMessage: "请再次输入新密码",
              }),
            }}
            rules={[
              {
                required: true,
                message: (
                  <FormattedMessage
                    id="pages.settings.password.confirmPassword.required"
                    defaultMessage="请确认新密码！"
                  />
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      intl.formatMessage({
                        id: "pages.settings.password.confirmPassword.notMatch",
                        defaultMessage: "两次输入的密码不一致！",
                      })
                    )
                  );
                },
              }),
            ]}
          />
        </ProForm>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
